// src/lib/apiClient.ts
import { isErrorCode, messageForCode, type ErrorCode } from "./errorCodes";

type ApiErrorPayload = { error?: string; code?: string; extra?: unknown };

/**
 * A refusal from the Edge Function. `message` is chosen from `code` (Phase 12)
 * and is safe to show; the server's own sentence is not kept.
 */
export class ApiError extends Error {
  status: number;
  code: ErrorCode | null;
  extra?: unknown;

  constructor(status: number, code: ErrorCode | null, extra?: unknown) {
    super(messageForCode(code));
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.extra = extra;
  }
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/**
 * We always call the router function by its canonical local URL:
 *   {SUPABASE_URL}/functions/v1/api/<route>
 *
 * e.g. /functions/v1/api/sync, /functions/v1/api/notes/send
 */
function buildUrl(route: string) {
  const clean = route.startsWith("/") ? route : `/${route}`;
  return `${SUPABASE_URL}/functions/v1/api${clean}`;
}

async function parseJsonSafe(res: Response) {
  const text = await res.text().catch(() => "");
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export async function apiPost<TResponse>(
  route: string,
  accessToken: string,
  body?: unknown
): Promise<TResponse> {
  const res = await fetch(buildUrl(route), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${accessToken}`,
    },
    body: body === undefined ? "{}" : JSON.stringify(body),
  });

  const json = (await parseJsonSafe(res)) as TResponse | ApiErrorPayload | null;

  if (!res.ok) {
    // The page shows a message chosen from the code, never the server's
    // sentence (Phase 12; errorCodes.ts).
    const payload = json as ApiErrorPayload | null;
    const code = isErrorCode(payload?.code) ? payload!.code as ErrorCode : null;
    throw new ApiError(res.status, code, payload?.extra);
  }

  return json as TResponse;
}
